// src/pages/admin/ProductManagement.jsx
import { useState, useEffect } from "react";
import { api } from "../../utils/api";

export default function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "Bouquets",
    stock: "",
    images: [""],
    featured: false,
    isCustomizable: false,
    customizationNote: "",
    size: "N/A",
    packSize: 1,
  });

  const categories = [
    "Bouquets", "Frames", "String Art", "Clay Items", "Accessories",
    "Phone Cases", "Cups", "Mini Items", "Cards", "Hampers"
  ];

  const sizes = ["Small", "Medium", "Large", "A5", "Standard", "N/A"];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    const products = await api.getProducts();
    setProducts(products);
    setLoading(false);
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        stock: product.stock,
        images: product.images || [""],
        featured: product.featured,
        isCustomizable: product.isCustomizable,
        customizationNote: product.customizationNote || "",
        size: product.size || "N/A",
        packSize: product.packSize || 1,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        category: "Bouquets",
        stock: "",
        images: [""],
        featured: false,
        isCustomizable: false,
        customizationNote: "",
        size: "N/A",
        packSize: 1,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productData = {
      ...formData,
      price: Number(formData.price),
      stock: Number(formData.stock),
      packSize: Number(formData.packSize),
      images: formData.images.filter(img => img.trim() !== ""),
    };

    let res;
    if (editingProduct) {
      res = await api.updateProduct(editingProduct._id, productData);
    } else {
      res = await api.createProduct(productData);
    }

    if (res.success) {
      alert(editingProduct ? "Product updated!" : "Product created!");
      setShowModal(false);
      fetchProducts();
    } else {
      alert(res.message || "Failed to save product");
    }
  };

  const handleDelete = async (id, name) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;

    const res = await api.deleteProduct(id);
    if (res.success) {
      alert("Product deleted!");
      fetchProducts();
    } else {
      alert(res.message || "Failed to delete product");
    }
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({ ...formData, images: newImages });
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ""] });
  };

  const removeImageField = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition font-semibold"
        >
          + Add Product
        </button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <div key={product._id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <img
              src={product.images?.[0] || "/placeholder.png"}
              alt={product.name}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-lg text-gray-900">{product.name}</h3>
                {product.featured && (
                  <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded">
                    ⭐ Featured
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                {product.description}
              </p>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xl font-bold text-purple-600">
                  ₹{product.price.toLocaleString()}
                </span>
                <span className={`text-sm ${product.stock > 0 ? "text-green-600" : "text-red-600"}`}>
                  Stock: {product.stock}
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                <span className="bg-gray-100 px-2 py-1 rounded">{product.category}</span>
                {product.size !== "N/A" && (
                  <span className="bg-gray-100 px-2 py-1 rounded ml-1">{product.size}</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenModal(product)}
                  className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product._id, product.name)}
                  className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 transition text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No products yet. Click "Add Product" to create one!
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  placeholder="Beautiful Handmade Bouquet"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  rows="3"
                  placeholder="Describe your product..."
                />
              </div>

              {/* Price & Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Category & Size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size
                  </label>
                  <select
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  >
                    {sizes.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pack Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pack Size
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.packSize}
                  onChange={(e) => setFormData({ ...formData, packSize: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Images (URLs) *
                </label>
                {formData.images.map((image, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="url"
                      required
                      value={image}
                      onChange={(e) => handleImageChange(index, e.target.value)}
                      className="flex-1 p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      placeholder="https://example.com/image.jpg"
                    />
                    {formData.images.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeImageField(index)}
                        className="px-3 bg-red-100 text-red-600 rounded hover:bg-red-200"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addImageField}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  + Add Another Image
                </button>
              </div>

              {/* Checkboxes */}
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Product</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isCustomizable}
                    onChange={(e) => setFormData({ ...formData, isCustomizable: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Customizable</span>
                </label>
              </div>

              {/* Customization Note */}
              {formData.isCustomizable && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customization Note
                  </label>
                  <input
                    type="text"
                    value={formData.customizationNote}
                    onChange={(e) => setFormData({ ...formData, customizationNote: e.target.value })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    placeholder="e.g., Choose your colors and text"
                  />
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition font-semibold"
                >
                  {editingProduct ? "Update Product" : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}